import React from 'react';
import { BookOpen } from '@phosphor-icons/react';

interface Enrollment {
  id?: number;
  course_name?: string;
  courseName?: string;
  state?: string;
  due_date?: string;
  dueDate?: string;
  mode?: string;
  category?: string;
  duration_in_minutes?: number;
  durationInMinutes?: number;
  progress_percentage?: number;
  progressPercentage?: number;
  completed_modules?: number;
  completedModules?: number;
  total_modules?: number;
  totalModules?: number;
}

interface Stats {
  total: number;
  not_started: number;
  in_progress: number;
  completed: number;
  overdue: number;
}

interface LearningPlanCardProps {
  data: { enrollments: Enrollment[]; stats: Stats | null } | Enrollment[] | null;
  options?: { show_stats?: boolean; max_items?: number };
}

function getField<T>(enrollment: Enrollment, snake: keyof Enrollment, camel: keyof Enrollment): T | undefined {
  return (enrollment[snake] ?? enrollment[camel]) as T | undefined;
}

function getProgressInfo(enrollment: Enrollment): { percentage: number; color: string; label: string } {
  const state = enrollment.state || '';
  const progress = getField<number>(enrollment, 'progress_percentage', 'progressPercentage');
  const completedModules = getField<number>(enrollment, 'completed_modules', 'completedModules');
  const totalModules = getField<number>(enrollment, 'total_modules', 'totalModules');

  let percentage = 0;
  let label = '';

  if (progress != null) {
    percentage = progress;
  } else if (completedModules != null && totalModules != null && totalModules > 0) {
    percentage = Math.round((completedModules / totalModules) * 100);
  } else {
    // Derive from state
    switch (state) {
      case 'Completed':
        percentage = 100;
        break;
      case 'On Going':
        percentage = 50;
        break;
      case 'Waiting for Confirmation':
        percentage = 90;
        break;
      default:
        percentage = 0;
    }
  }

  if (completedModules != null && totalModules != null) {
    label = `${completedModules} of ${totalModules} modules`;
  } else {
    label = state || 'Not Started';
  }

  // Color based on percentage and state
  let color = '#94a3b8'; // gray for not started
  if (state === 'Completed' || percentage === 100) {
    color = '#14b8a6'; // teal/green
  } else if (percentage >= 60) {
    color = '#3b82f6'; // blue
  } else if (percentage > 0) {
    color = '#f59e0b'; // amber/orange
  }

  return { percentage, color, label };
}

function formatDueDate(enrollment: Enrollment): { text: string; isOverdue: boolean; isCompleted: boolean } {
  const state = enrollment.state || '';
  if (state === 'Completed') {
    return { text: 'Completed', isOverdue: false, isCompleted: true };
  }

  const dueDateStr = getField<string>(enrollment, 'due_date', 'dueDate');
  if (!dueDateStr) return { text: '', isOverdue: false, isCompleted: false };

  const dueDate = new Date(dueDateStr);
  const now = new Date();
  const isOverdue = dueDate < now;

  const month = dueDate.toLocaleString('en-US', { month: 'short' });
  const day = dueDate.getDate();

  return { text: `Due: ${month} ${day}`, isOverdue, isCompleted: false };
}

export const LearningPlanCard: React.FC<LearningPlanCardProps> = ({ data, options }) => {
  // Normalize data
  let enrollments: Enrollment[] = [];
  let stats: Stats | null = null;

  if (data) {
    if (Array.isArray(data)) {
      enrollments = data;
    } else if (data.enrollments) {
      enrollments = data.enrollments;
      stats = data.stats || null;
    }
  }

  const maxItems = options?.max_items || 10;
  const displayEnrollments = enrollments.slice(0, maxItems);

  // Count active (non-completed) paths
  const activeCount = enrollments.filter(e => e.state !== 'Completed').length;

  // Empty state
  if (enrollments.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '32px 16px',
        color: '#94a3b8',
      }}>
        <BookOpen size={48} weight="thin" style={{ marginBottom: 12 }} />
        <div style={{ fontSize: '15px', fontWeight: 500, color: '#64748b' }}>
          No learning plans assigned
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: 4 }}>
          Your assigned courses will appear here
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {stats ? (
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#14b8a6',
              backgroundColor: '#f0fdfa',
              padding: '2px 8px',
              borderRadius: '9999px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
            }}>
              Ready
            </span>
          ) : null}
        </div>
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>
          {activeCount} active path{activeCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Enrollment List */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {displayEnrollments.map((enrollment, idx) => {
          const name = getField<string>(enrollment, 'course_name', 'courseName') || 'Untitled Course';
          const progress = getProgressInfo(enrollment);
          const due = formatDueDate(enrollment);

          return (
            <div key={enrollment.id || idx}>
              {/* Course name + percentage */}
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: '6px',
              }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
                  {name}
                </span>
                <span style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: progress.color,
                  marginLeft: '12px',
                  flexShrink: 0,
                }}>
                  {progress.percentage}%
                </span>
              </div>

              {/* Progress bar */}
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e2e8f0',
                borderRadius: '9999px',
                overflow: 'hidden',
                marginBottom: '4px',
              }}>
                <div style={{
                  width: `${progress.percentage}%`,
                  height: '100%',
                  backgroundColor: progress.color,
                  borderRadius: '9999px',
                  transition: 'width 0.3s ease',
                }} />
              </div>

              {/* Module count + due date */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '12px',
              }}>
                <span style={{ color: '#94a3b8' }}>
                  {progress.label}
                </span>
                {due.text && (
                  <span style={{
                    color: due.isCompleted ? '#14b8a6' : due.isOverdue ? '#ef4444' : '#94a3b8',
                    fontWeight: due.isCompleted || due.isOverdue ? 600 : 400,
                  }}>
                    {due.text}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LearningPlanCard;
